import React, { useState, useEffect, useCallback } from 'react';
import { PhoneOff, Plus, Search, Shield, AlertTriangle, ToggleLeft, ToggleRight, Loader2, Globe, User, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../../services/api';

const MobileBlocking = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [form, setForm] = useState({ mobile_numbers: '', scope: 'GLOBAL', user_id: '', reason: '', is_active: true });
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/global-settings/mobile-block');
      setRules(res.data?.data || []);
    } catch (e) { showMsg('error', 'Failed to fetch mobile block rules'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleAdd = async () => {
    if (!form.mobile_numbers.trim()) { showMsg('error', 'Enter at least one mobile number'); return; }
    if (form.scope === 'USER' && !form.user_id) { showMsg('error', 'User ID is required for USER scope'); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, is_active: form.is_active, user_id: form.scope === 'USER' ? parseInt(form.user_id) : null };
      await api.post('/admin/global-settings/mobile-block', payload);
      showMsg('success', 'Mobile numbers blocked successfully');
      setShowAdd(false); setForm({ mobile_numbers: '', scope: 'GLOBAL', user_id: '', reason: '', is_active: true });
      fetchRules();
    } catch (e) { showMsg('error', e.response?.data?.error || 'Failed to add'); }
    finally { setSubmitting(false); }
  };

  const handleToggle = async (id) => {
    try {
      await api.post(`/admin/global-settings/mobile-block/${id}/toggle`);
      fetchRules();
    } catch (e) { showMsg('error', 'Failed to toggle rule'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this mobile block rule?')) return;
    try {
      await api.delete(`/admin/global-settings/mobile-block/${id}`);
      showMsg('success', 'Rule deactivated');
      fetchRules();
    } catch (e) { showMsg('error', 'Failed to delete'); }
  };

  const filtered = rules.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return r.mobile_number?.toLowerCase().includes(s) || (r.reason || '').toLowerCase().includes(s) || r.scope?.toLowerCase().includes(s) || (r.username || '').toLowerCase().includes(s);
  });
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const globalCount = rules.filter(r => r.scope === 'GLOBAL' && r.is_active).length;
  const userCount = rules.filter(r => r.scope === 'USER' && r.is_active).length;

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-red-100 rounded-xl"><PhoneOff className="w-6 h-6 text-red-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mobile Number Blocking</h1>
            <p className="text-sm text-gray-500">Block mobile numbers globally or per entity/user. Blocked numbers are rejected before SMPP submission.</p>
          </div>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
          <Plus className="w-4 h-4" /> Add Block Rule
        </button>
      </div>

      {msg.text && (
        <div className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${msg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[10px] font-bold text-gray-400 uppercase">Total Rules</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{rules.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-blue-500" /><p className="text-[10px] font-bold text-gray-400 uppercase">Global Blocks</p></div>
          <p className="text-2xl font-bold text-blue-600 mt-1">{globalCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-purple-500" /><p className="text-[10px] font-bold text-gray-400 uppercase">User-Specific</p></div>
          <p className="text-2xl font-bold text-purple-600 mt-1">{userCount}</p>
        </div>
      </div>

      {showAdd && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 space-y-3">
          <h3 className="font-semibold text-gray-900">Add Mobile Block Rules</h3>
          <p className="text-xs text-gray-500">Enter one or more mobile numbers separated by commas or newlines. Numbers will be auto-normalized (91 prefix added if 10 digits).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <textarea value={form.mobile_numbers} onChange={e => setForm(f => ({ ...f, mobile_numbers: e.target.value }))}
              placeholder="9876543210, 9876543211&#10;or one per line" rows={3}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 font-mono" />
            <div className="space-y-2">
              <select value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="GLOBAL">Global (all users)</option>
                <option value="USER">User-Specific</option>
              </select>
              {form.scope === 'USER' && (
                <input type="number" value={form.user_id} onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))}
                  placeholder="User ID" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              )}
              <input type="text" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Reason (optional)" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg">Cancel</button>
            <button onClick={handleAdd} disabled={submitting} className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Block Numbers
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-800">Blocked Mobile Numbers</h3>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 w-56" />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Shield className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>{search ? 'No results found' : 'No mobile block rules yet'}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50">
                  {['Mobile Number', 'Scope', 'User', 'Reason', 'Status', 'Created', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {paginated.map(r => (
                    <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                      <td className="px-4 py-2.5 font-mono text-xs font-semibold text-gray-800">{r.mobile_number}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${r.scope === 'GLOBAL' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                          {r.scope}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600">{r.username || (r.user_id ? `#${r.user_id}` : '-')}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{r.reason || '-'}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${r.is_active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                          {r.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleToggle(r.id)} title={r.is_active ? 'Deactivate' : 'Activate'}
                            className="p-1 rounded hover:bg-gray-100">
                            {r.is_active ? <ToggleRight className="w-5 h-5 text-green-600" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">Showing {Math.min((page-1)*pageSize+1, filtered.length)}-{Math.min(page*pageSize, filtered.length)} of {filtered.length}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-xs text-gray-600 px-2">Page {page} of {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900 text-sm mb-1">How Mobile Blocking Works</h3>
            <ul className="text-amber-800 text-xs space-y-1">
              <li><strong>Global:</strong> Blocks the number for all users across the platform.</li>
              <li><strong>User-Specific:</strong> Blocks the number only for a specific user/entity.</li>
              <li>Blocked numbers are checked before SMPP submission — no performance impact on the sending pipeline.</li>
              <li>Toggle rules on/off without deleting them for quick management.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileBlocking;
